import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface AnalyticsData {
  total_messages: number;
  total_users: number;
  total_facts: number;
  messages_today: number;
  misinformation_count: number;
  language_distribution: Record<string, number>;
  topic_distribution: Record<string, number>;
  daily_messages: { date: string; count: number }[];
}

export default function Analytics() {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getAnalytics()
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-slate-500">Loading analytics...</div>;
  if (error) return <div className="text-center py-12 text-red-400">Error: {error}</div>;
  if (!data) return null;

  const langEntries = Object.entries(data.language_distribution || {}).sort((a, b) => b[1] - a[1]);
  const langTotal = langEntries.reduce((s, [, c]) => s + c, 0) || 1;
  const topicEntries = Object.entries(data.topic_distribution || {}).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const topicTotal = topicEntries.reduce((s, [, c]) => s + c, 0) || 1;
  const maxDaily = Math.max(...(data.daily_messages || []).map(d => d.count), 1);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <p className="text-slate-400 text-sm mt-1">Real-time usage and conversation insights</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Messages', value: data.total_messages, color: 'text-cyan-400' },
          { label: 'Today', value: data.messages_today, color: 'text-emerald-400' },
          { label: 'Users', value: data.total_users, color: 'text-purple-400' },
          { label: 'Facts', value: data.total_facts, color: 'text-blue-400' },
          { label: 'Flagged', value: data.misinformation_count, color: 'text-red-400' },
        ].map(s => (
          <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="text-xs text-slate-400 uppercase tracking-wider">{s.label}</div>
            <div className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-4">Language Distribution</h2>
          {langEntries.length === 0 ? (
            <div className="text-slate-500 text-sm">No data yet</div>
          ) : (
            <div className="space-y-3">
              {langEntries.map(([lang, count]) => (
                <div key={lang}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-300 capitalize">{lang}</span>
                    <span className="text-slate-400">{count} · {((count / langTotal) * 100).toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full" style={{ width: `${(count / langTotal) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-4">Top Topics</h2>
          {topicEntries.length === 0 ? (
            <div className="text-slate-500 text-sm">No topics detected yet</div>
          ) : (
            <div className="space-y-2">
              {topicEntries.map(([topic, count], i) => (
                <div key={topic} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 text-xs w-4">{i + 1}.</span>
                    <span className="text-slate-300 text-sm capitalize">{topic.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500/60 rounded-full" style={{ width: `${(count / topicTotal) * 100}%` }} />
                    </div>
                    <span className="text-slate-400 text-xs w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-white mb-4">Messages — Last 30 Days</h2>
        {data.daily_messages && data.daily_messages.length > 0 ? (
          <div className="flex items-end gap-1 h-40">
            {data.daily_messages.map(d => (
              <div key={d.date} className="flex-1 group relative" title={`${d.date}: ${d.count}`}>
                <div
                  className="w-full bg-gradient-to-t from-cyan-500/40 to-cyan-400 rounded-t hover:from-cyan-500/60 hover:to-cyan-300 transition-all"
                  style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: '2px' }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-slate-500 text-sm">No daily data yet</div>
        )}
      </div>
    </div>
  );
}
